import {
    otherResultFormula,
    otherResultOrderBehavior,
    type OtherResultOrderBehavior,
} from '@/orm/catalogs/OtherResults'
import { tcs } from '@/plugins/i18nFormat'
import nvl from '@/utils/nvl'
import Sandbox from '@nyariv/sandboxjs'

/**
 * Shared scoring helpers for result-like models (EventResult, EventHeatResult).
 *
 * These functions are intentionally stateless - both the compiled-formula cache and the
 * per-instance points cache live in the concrete models and are passed in by reference.
 *
 * The "effective order" abstracts the single point of variation between the models:
 * EventResult passes its handicap-adjusted orderInclHandicap, elimination heats pass the plain
 * order (handicaps never apply in a heat).
 */

export type CompiledFormula = (ctx: { boats: number; order: number; attr: number }) => { run: () => number }
export type FormulaCache = Map<string, CompiledFormula>
export type PointsCache = { key: string; value: number } | undefined

/** Value shown in the result column: the explicit "other" code, otherwise the effective order. */
export function resultValue(other: string | null, effectiveOrder: number | null): string | number | null {
    return nvl(other, effectiveOrder)
}

export type ResultFormulaInfo = {
    needOrder: boolean
    needAttr: boolean
    attrType: 'percent' | 'points' | null
    attrDefault: number
    orderBehavior: OtherResultOrderBehavior
}

/**
 * Parse the scoring formula of a result code into what the entry UI needs to know.
 * `otherCode === null` means a plain order finish. Returns null for an unknown code.
 */
export function parseResultFormula(otherCode: string | null): ResultFormulaInfo | null {
    const formula = otherCode === null ? 'order' : otherResultFormula(otherCode)

    if (formula === null)
        return null

    const matches = formula.match(/attr\[\s*(percent|points)\s*(?:\|\s*(\d+))?\s*\]/)

    return {
        needOrder: formula.includes('order'),
        needAttr: formula.includes('attr'),
        attrType: matches ? (matches[1] as 'percent' | 'points') : null,
        attrDefault: (matches && matches[2] !== undefined) ? parseInt(matches[2]) : 0,
        orderBehavior: otherCode === null ? 'clear' : (otherResultOrderBehavior(otherCode) ?? 'clear'),
    }
}

/**
 * Points for a result.
 *
 * @param rawOrder        the stored order (used only to decide the plain-order fast path)
 * @param effectiveOrder  the order used for scoring (handicap-adjusted for EventResult, plain for heats)
 * @param formulaCache    the caller's compiled-formula cache (mutated in place)
 * @param pointsCache     the caller's last memoized points entry
 * @param inHeat          true when scoring within an elimination heat (see the `boats + 1` note below)
 * @returns the points value and the points cache entry the caller should store back
 */
export function resultPoints(
    rawOrder: number | null,
    effectiveOrder: number | null,
    other: string | null,
    attr: number | null,
    totalBoats: number,
    formulaCache: FormulaCache,
    pointsCache: PointsCache,
    inHeat: boolean = false,
): { value: number; cache: PointsCache } {
    if (rawOrder !== null && other === null)
        return { value: effectiveOrder as number, cache: pointsCache }

    const formula = other === null ? null : otherResultFormula(other === 'T1B' ? 'T1A' : other)

    if (formula === null)
        return { value: nvl(effectiveOrder, totalBoats), cache: pointsCache }

    const order = nvl(effectiveOrder, totalBoats)
    const attrValue = nvl(attr, totalBoats)

    // Every "other result" formula expresses its worst score / penalty ceiling as `boats + 1`.
    // Inside an elimination heat that ceiling is the plain number of boats in the heat (an
    // exception), so we feed the formula one fewer boat and `boats + 1` collapses to exactly the
    // heat size. `order` / `attr` keep the real boat count for their own fallbacks.
    const formulaBoats = inHeat ? totalBoats - 1 : totalBoats

    const key = [other ?? 'ORD', totalBoats, order, attrValue, inHeat].join('|')

    if (pointsCache && pointsCache.key === key)
        return { value: pointsCache.value, cache: pointsCache }

    let exec = formulaCache.get(formula)

    if (!exec) {
        const sandbox = new Sandbox()

        exec = sandbox.compile('return ' + formula.replace(/attr\[.*?\]/, 'attr'))

        formulaCache.set(formula, exec)
    }

    const value = exec({ boats: formulaBoats, order, attr: attrValue }).run()

    return { value, cache: { key, value } }
}

/** Human-readable info for the result attribute (percent / points / plain value). */
export function resultAttrInfo(attr: number | null, other: string | null): string | number {
    if (attr === null)
        return ''

    if (other === null)
        return attr

    const formula = other === null ? null : otherResultFormula(other === 'T1B' ? 'T1A' : other)

    if (formula === null)
        return attr

    const matches = formula.match(/attr\[\s*(percent|points).*?\]/)

    if (!matches)
        return attr

    return matches[1] === 'points' ? tcs('pointsNo', attr) : Math.round(attr * 100) + ' %'
}

/** Comparator by order, then by "other" code. Handicap (finish-time) ordering is caller-specific. */
export function compareByOrder(
    orderA: number | null,
    orderB: number | null,
    otherA: string | null,
    otherB: string | null,
): number {
    if (orderA !== null && orderB !== null)
        return orderA - orderB

    if (orderA !== null)
        return -1

    if (orderB !== null)
        return 1

    if (otherA === null || otherB === null)
        return 0

    return otherA > otherB ? 1 : (otherA < otherB ? -1 : 0)
}
